import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import BasicEditProduct from "./BasicEditProduct/BasicEditProduct";
import AdvancedEditProduct from "./AdvancedEditProduct/AdvancedEditProduct";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { GET_PRODUCT_SCHEDULE_TYPES_AND_PERIODICITIES, QUOTE_QUERY } from "features/quote/queries";
import { emptyArray, emptyObject } from "constants/defaultValues";
import { COMPUTE_SCHEDULE, SAVE_PRODUCT } from "../mutations";
import { useApolloClient } from "@apollo/client";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import { useRulesLens, set, get } from "@totalsoft/rules-algebra-react";
import * as V from "@totalsoft/pure-validations";
import * as R from "@totalsoft/rules-algebra";
import { INTEREST_TYPE_DETAIL, QUOTE_PRODUCT_QUERY } from "../queries";
import { useParams } from "react-router-dom";
import { useDirtyFieldValidation } from "@totalsoft/pure-validations-react";
import moment from "moment";
import { INIT_PARAM_VALUE_QUERY, PRODUCT_SERVICE_TYPES } from "features/common/queries";
import { useTranslation } from "react-i18next";
import { addToast } from "components/notifications";
import useDebounce from "hooks/useDebounce";
import { invalidateApolloClientCache } from "utils/functions";
import { getSettingsRules, getProductRules, getProductValidator } from "features/products/rules";
import { sequence } from "@totalsoft/react-state-lens";
import { isDirty } from "@totalsoft/change-tracking";
import { getTenant } from "providers/TenantProvider";
import { omit } from "lodash";

function EditProductContainer({ openEditProductLens, quoteProductId }) {
  const client = useApolloClient();
  const params = useParams();
  const { t } = useTranslation();
  const quoteId = parseInt(params.quoteId);
  const { countryCode } = getTenant();
  const [autoAddedInsuranceTypes, setAutoAddedInsuranceTypes] = useState(false);

  const { data: quoteData, loading: loadingQuote } = useQueryWithErrorHandling(QUOTE_QUERY, {
    variables: { quoteId },
  });
  const { quote } = quoteData;

  useQueryWithErrorHandling(INIT_PARAM_VALUE_QUERY, {
    variables: { paramName: "VCP_AutoAddedInsuranceTypes" },
    onCompleted: (data) => {
      if (!data) return;
      if (data.initializationParamValue.value === null || data.initializationParamValue.value.toUpperCase() === "NONE")
        return;
      setAutoAddedInsuranceTypes(true);
    },
  });

  const settingsRules = useMemo(() => getSettingsRules(countryCode), [countryCode]);
  const [settingsLens] = useRulesLens(settingsRules, {
    product: { quoteProductServices: emptyArray, cellParamsConfigurations: emptyArray },
    useDownPaymentPercent: true,
    useResidualPercent: true,
    useInterestPercent: true,
    showProductSchedule: false,
    showInsurancePage: false,
    years: emptyArray,
    cellParamConfigurations: emptyArray,
    quoteProductServices: emptyArray,
    productServiceTypes: emptyArray,
    showMixedConfiguredOptionsWarning: false,
  });
  const settings = settingsLens |> get;

  const productRules = useMemo(() => getProductRules(settings.productServiceTypes), [settings.productServiceTypes]);
  const [productLens, productDirtyInfo, resetProductLens] = useRulesLens(productRules, { quoteProductId });
  const product = (productLens |> get) || emptyObject;
  const productDebounced = useDebounce(product, 300);
  const productIsDirty = isDirty(productDirtyInfo);

  useQueryWithErrorHandling(PRODUCT_SERVICE_TYPES, {
    variables: { productId: product?.productId },
    skip: !product?.productId,
    onCompleted: (data) => {
      (settingsLens.productServiceTypes |> set)(data.productServiceTypes);
    },
  });

  const productValidator = useMemo(() => getProductValidator(settingsLens |> get), [settingsLens]);
  const [productValidation, validateProduct] = useDirtyFieldValidation(productValidator);

  const { data: quoteWithProductData, loading: loadingProduct } = useQueryWithErrorHandling(QUOTE_PRODUCT_QUERY, {
    variables: { quoteProductId },
    onCompleted: (data) => {
      if (!data) return;
      (settingsLens.useDownPaymentPercent |> set)(data.quoteProduct.downPaymentUsePercent);
      (settingsLens.useResidualPercent |> set)(data.quoteProduct.residualValueUsePercent);
    },
    skip: !quote,
  });

  useEffect(() => {
    if (productIsDirty || !quoteWithProductData?.quoteProduct) return;
    const quoteRating = quote?.rating;
    const quoteProduct = quoteWithProductData?.quoteProduct;
    const newQuoteProduct = R.applyRule(
      productRules,
      { ...quoteProduct, quoteRating },
      { ...quoteProduct, quoteRating }
    );
    resetProductLens(newQuoteProduct);
  }, [resetProductLens, productIsDirty, quoteWithProductData?.quoteProduct, productRules, quote?.rating]);

  const { data: listsData, loading: loadingLists } = useQueryWithErrorHandling(
    GET_PRODUCT_SCHEDULE_TYPES_AND_PERIODICITIES,
    {
      variables: { productId: product?.productId, paymentFrequency: product?.paymentFrequency },
      notifyOnNetworkStatusChange: true,
      skip: !product?.productId,
    }
  );
  const productScheduleTypes = listsData?.productScheduleTypes || emptyArray;
  const productPeriodicities = listsData?.productPeriodicities || emptyArray;

  const interestTypeDetailInput = useMemo(() => {
    if (loadingProduct || !product?.quoteId) return null;
    return {
      quoteId: product.quoteId,
      quoteProductId: product.quoteProductId,
      duration: product.duration,
      periodicity: product.periodicity,
      residualValuePercent: Number(product.residualValuePercent),
    };
  }, [
    loadingProduct,
    product.quoteId,
    product.quoteProductId,
    product.duration,
    product.periodicity,
    product.residualValuePercent,
  ]);
  const interestTypeDetailInputDebounced = useDebounce(interestTypeDetailInput, 300);
  useQueryWithErrorHandling(INTEREST_TYPE_DETAIL, {
    variables: interestTypeDetailInputDebounced,
    skip: !interestTypeDetailInputDebounced,
    onCompleted: (data) => {
      if (!data?.interestTypeDetail || !productIsDirty) return;
      (productLens.floatingInterestPercent |> set)(data?.interestTypeDetail?.floatingInterestPercent);
      (productLens.bankMargin |> set)(data?.interestTypeDetail?.bankMargin);
      (productLens.bankMargin_PeriodMin |> set)(data?.interestTypeDetail?.bankMargin_PeriodMin);
      (productLens.bankMargin_PeriodMax |> set)(data?.interestTypeDetail?.bankMargin_PeriodMax);
      (productLens.variableInterestDate |> set)(data?.interestTypeDetail?.variableInterestDate);
    },
  });

  const [saveProduct, { loading: saving }] = useMutationWithErrorHandling(SAVE_PRODUCT, {
    onError: () => {
      invalidateApolloClientCache(client, { __typename: "Quote", quoteId });
    },
  });

  const handleSave = useCallback(async () => {
    if (!V.isValid(productValidation)) {
      addToast(`${t("General.InvalidValues")}`, "warning");
      return;
    }
    const input = {
      quoteProductId: productLens.quoteProductId |> get,
      scheduleTypeId: productLens.scheduleTypeId |> get,
      duration: productLens.duration |> get,
      periodicity: productLens.periodicity |> get,
      startDate: moment(productLens.firstInstallmentDate |> get).format("YYYY-MM-DDT00:00:00"),
      downPaymentAmount: productLens.downPaymentAmount |> get,
      downPaymentAmountWithVAT: productLens.downPaymentAmountWithVAT |> get,
      downPaymentPercent: productLens.downPaymentPercent |> get,
      downPaymentUsePercent: settingsLens.useDownPaymentPercent |> get,
      rentalDownPaymentAmount: productLens.rentalDownPaymentAmount |> get,
      useRentalDownPayment: productLens.useRentalDownPayment |> get,
      residualValueAmount: productLens.residualValueAmount |> get,
      residualValueAmountWithVAT: productLens.residualValueAmountWithVAT |> get,
      residualValueUsePercent: settingsLens.useResidualPercent |> get,
      residualValuePercent: productLens.residualValuePercent |> get,
      residualValueMinPercent: productLens.residualValueMinPercent |> get,
      residualValueMaxPercent: productLens.residualValueMaxPercent |> get,
      residualValueVisible: productLens.residualValueVisible |> get,
      residualValueIsReadonly: productLens.residualValueIsReadonly |> get,
      vatFinancing: productLens.vatFinancing |> get,
      addInsurance: productLens.addInsurance |> get,
      dealerCommissionValue: productLens.dealerCommissionValue |> get,
      subsidyValue: productLens.subsidyValue |> get,
      interestPercent: productLens.interestPercent |> get,
      flatRate: productLens.flatRate |> get,
      floatingInterestPercent: productLens.floatingInterestPercent |> get,
      bankMargin: productLens.bankMargin |> get,
      bankMargin_PeriodMin: productLens.bankMargin_PeriodMin |> get,
      bankMargin_PeriodMax: productLens.bankMargin_PeriodMax |> get,
      variableInterestDate: productLens.variableInterestDate |> get,
      ratingBasedInterestPercent: productLens.ratingBasedInterestPercent |> get,
      quoteProductServices: (productLens.quoteProductServices |> get).map((quoteProductService) => ({
        quoteProductId: quoteProductService.quoteProductId,
        serviceTypeId: quoteProductService.serviceTypeId,
        serviceTypeCode: quoteProductService.serviceTypeCode,
        serviceTypeName: quoteProductService.serviceTypeName,
        calcTypeId: quoteProductService.calcTypeId,
        serviceAmount: quoteProductService.serviceAmount,
        servicePercent: quoteProductService.servicePercent,
        periodicity: quoteProductService.periodicity,
        financedYN: quoteProductService.financedYN,
        duration: quoteProductService.duration,
      })),
      cellParamsConfigurations: productLens.cellParamsConfigurations |> get,
    };
    const result = await saveProduct({ variables: { input } });
    if (!result.data) return;

    invalidateApolloClientCache(client, { __typename: "Quote", quoteId: parseInt(quoteId) }, ["quoteProductHistories"]);

    resetProductLens({ ...omit(result.data.saveProduct, ["productSchedule"]) });

    (settingsLens.years |> sequence).forEach((yearLens) => {
      (yearLens.months |> sequence).forEach((monthLens) => (monthLens.selected |> set)(false));
    });

    if (result.data.saveProduct.savedWithError) addToast(result.data.saveProduct.error, "warning");
    if (result.data.saveProduct.insuranceMessageRequired)
      addToast(result.data.saveProduct.calculateInsuranceMessage, "warning");
  }, [
    client,
    productLens,
    saveProduct,
    t,
    productValidation,
    resetProductLens,
    quoteId,
    settingsLens.years,
    settingsLens.useDownPaymentPercent,
    settingsLens.useResidualPercent,
  ]);

  const [computeSchedule] = useMutationWithErrorHandling(COMPUTE_SCHEDULE, {
    onError: () => {
      invalidateApolloClientCache(client, { __typename: "Quote", quoteId });
    },
  });
  const handleComputeSchedule = useCallback(async () => {
    const result = await computeSchedule({
      variables: {
        quoteId: quoteId,
        quoteProductId: productLens.quoteProductId |> get,
        addInsurance: productLens.addInsurance |> get,
      },
    });
    if (productIsDirty) resetProductLens(productLens |> get);
    if (result.data?.computeSchedule?.savedWithError) addToast(result.data.computeSchedule.error, "warning", false);
    if (result.data?.computeSchedule?.insuranceMessageRequired)
      addToast(result.data.computeSchedule.calculateInsuranceMessage, "warning");
  }, [productIsDirty, productLens, computeSchedule, resetProductLens, quoteId]);

  useEffect(() => {
    validateProduct(product, productDirtyInfo);
  }, [validateProduct, product, productDirtyInfo]);

  useEffect(() => {
    if (JSON.stringify(settingsLens.product |> get) !== JSON.stringify(productDebounced)) {
      (settingsLens.product |> set)(productDebounced);
    }
  }, [productDebounced, settingsLens.product]);

  const loading = loadingProduct || loadingLists || loadingQuote || !quote;

  return (
    <>
      {(openEditProductLens |> get) === "BASIC" && (
        <BasicEditProduct
          openEditProductLens={openEditProductLens}
          quote={quote}
          productLens={productLens}
          settingsLens={settingsLens}
          productDirtyInfo={productDirtyInfo}
          productValidation={productValidation}
          productScheduleTypes={productScheduleTypes}
          productPeriodicities={productPeriodicities}
          onSave={handleSave}
          onComputeSchedule={handleComputeSchedule}
          loading={loading}
          saving={saving}
          autoAddedInsuranceTypes={autoAddedInsuranceTypes}
        />
      )}
      {(openEditProductLens |> get) === "ADVANCED" && (
        <AdvancedEditProduct
          openEditProductLens={openEditProductLens}
          quote={quote}
          productLens={productLens}
          productDirtyInfo={productDirtyInfo}
          productValidation={productValidation}
          productScheduleTypes={productScheduleTypes}
          productPeriodicities={productPeriodicities}
          onSave={handleSave}
          onComputeSchedule={handleComputeSchedule}
          loading={loading}
          saving={saving}
          settingsLens={settingsLens}
          autoAddedInsuranceTypes={autoAddedInsuranceTypes}
        />
      )}
    </>
  );
}

EditProductContainer.propTypes = {
  openEditProductLens: PropTypes.object.isRequired,
  quoteProductId: PropTypes.number.isRequired,
};

export default EditProductContainer;
